import { Component } from '@angular/core';
import { SelectCustomEvent } from '@ionic/angular';
import { Observable, of, Subscription, Subject, from, forkJoin } from 'rxjs';
import { Storage, ref, listAll, ListResult, StorageReference, getMetadata } from '@angular/fire/storage';
import { map, mergeMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment'; //!commented for offline version
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  IFirmwareFile,
  IParameterFile,
  FileListItem,
  GetFileListResponse,
} from '@shared/interfaces/firmware.interface';
import { IFirmwareUpgrade, IParameterUpgrade } from '@interfaces/request.interface';

import { NetworkService } from '@shared/services/network.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '@services/modal.service';
import { StorageService } from '@shared/services/storage.service';

@Component({
  selector: 'app-firmware-download',
  templateUrl: './firmware-download.component.html',
  styleUrl: './firmware-download.component.scss',
})
export class FirmwareDownloadComponent {
  public firmware: IFirmwareFile;
  public parameterFile: IParameterFile; //for parameter upload

  public items: any[] = []; //for firmware
  public paraItems: any[] = []; //for parameters

  public fileSelected: boolean = false;

  public isLoading: boolean = false;
  public softwareFileFB: StorageReference = null; //!for FB
  public parameterFileFB: StorageReference = null; //!for FB

  //for parameter upload

  public paraFileSelected: boolean = false;

  public isOnline: boolean = navigator.onLine;

  folders: string[] = ['Latest', 'Old']; // folders in   firebase
  selectedFolder: string = 'Latest';

  constructor(
    private angularFireStorage: Storage,
    private networkService: NetworkService,
    private translateService: TranslateService,
    private httpClient: HttpClient,
    private modalService: ModalService,
    private storageService: StorageService,
    private functions: Functions,
  ) {}

  ngOnInit(): void {
    this.fileSelected = false;

    this.paraFileSelected = false;

    // subscribing the network status service to know the current network status
    this.networkService.getOnlineStatus().subscribe((status) => {
      this.fileSelected = false;

      this.paraItems = [];
      this.items = [];
      this.paraFileSelected = false;

      this.isOnline = status; // Update the UI dynamically
      // console.log('Network status changed:', status ? 'Online' : 'Offline');

      // if online creating the storage reference for firmware and parameters

      if (this.isOnline) {
        /* for firmware */
        this.loadFolderContent();

        /* for parameter */
        this.loadParameters();
      }
    });
  }
  /*
  public loadFolderContent() {
    var folderLabel = '';
    this.items = [];
    this.fileSelected = false;

    this.isLoading = true;

    if (this.selectedFolder === 'Latest') {
      folderLabel = 'release';
    } else {
      folderLabel = 'old';
    }

    const firmwareRef = ref(this.angularFireStorage, `systemversion/3/0/${folderLabel}`);
    from(listAll(firmwareRef))
      .pipe(
        map((result: ListResult) => result.items),
        mergeMap((items: StorageReference[]) => {
          if (items.length === 0) {
            // No files – emit empty array immediately
            return of([]);
          }
          // If files exist, fetch metadata for each
          return forkJoin(
            items.map((item) =>
              from(getMetadata(item)).pipe(
                map((metaData) => {
                  const truncatedName = metaData.name.split('_').slice(1).join('_').replace('.json', '');
                  return { ...metaData, truncatedName };
                }),
              ),
            ),
          );
        }),
      )
      .subscribe({
        next: (result) => {
          this.items = result;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching metadata:', err);
          this.items = [];
          this.isLoading = false;
        },
      });
  }*/
  public async loadFolderContent() {
    let folderLabel = '';
    this.items = [];
    this.fileSelected = false;

    this.isLoading = true;

    folderLabel = this.selectedFolder === 'Latest' ? 'release' : 'old';

    try {
      const result = await this.getFileList('systemversion', `3/0/${folderLabel}`);

      this.items = result.files.map((file) => {
        const truncatedName = file.name.split('_').slice(1).join('_').replace('.json', '');
        return { ...file, truncatedName };
      });
    } catch (error) {
      console.error('Error fetching firmware list:', error);
      this.items = [];
    } finally {
      this.isLoading = false;
    }
  }
  // --- Parameters ---
  public async loadParameters() {
    try {
      const result = await this.getFileList('parameters', '3/release');

      this.paraItems = result.files.map((metaData) => {
        const truncatedParaName = metaData.name.split('_').slice(1).join('_').replace('.json', '');
        return { ...metaData, truncatedParaName };
      });
    } catch (error) {
      console.error('Error fetching parameters:', error);
      this.paraItems = [];
    }
  }

  // --- Shared helper ---
  private async getFileList(folder: string, subPath: string): Promise<GetFileListResponse> {
    const getList = httpsCallable<{ folder: string; subPath: string }, GetFileListResponse>(
      this.functions,
      'getFileList',
    );
    const result = await getList({ folder, subPath });
    return result.data;
  }

  //---------------------function to save firmware file locally------------------------------//
  public saveFirmware() {
    if (this.firmware) {
      const firmwareFileToSave = this.softwareFileFB;

      const jsonString = JSON.stringify(this.firmware, null, 2); // Pretty print with 2 spaces

      // Save the JSON file using the Save As dialog
      const blob = new Blob([jsonString], { type: 'application/json' });
      const fileName = firmwareFileToSave.fullPath.split('/').pop() || 'file.json';

      // Fallback: Save using anchor tag
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // console.log('File saved successfully using fallback!');
      setTimeout(() => {
        this.softwareFileFB = null;
      }, 1000);
    }
  }
  //---------------------function to save parameter file locally------------------------------//
  public saveParameter() {
    if (this.parameterFile) {
      const parameterFileToSave = this.parameterFileFB;

      const jsonString = JSON.stringify(this.parameterFile, null, 2); // Pretty print with 2 spaces

      // Save the JSON file using the Save As dialog
      const blob = new Blob([jsonString], { type: 'application/json' });
      const fileName = parameterFileToSave.fullPath.split('/').pop() || 'file.json';

      // Fallback: Save using anchor tag
      const a = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      //  console.log('File saved successfully using fallback!');
      setTimeout(() => {
        this.parameterFileFB = null;
      }, 500);
    }
  }

  //-------------------------triggering file upload----------//
  public triggerFileInput(): void {
    //console.log('triggerFileInput');
    const fileInput = document.querySelector<HTMLInputElement>('#fileInput');
    //console.log('triggerFileInput', fileInput);
    if (fileInput) {
      //  console.log('triggerFileInput clicked');
      fileInput.click();
    }
  }
  //---------------------function when firmware file selected------------------------------//

  public onFileSelected(event: SelectCustomEvent): void {
    const selected = event.detail.value;
    this.softwareFileFB = selected;
    this.fileSelected = !!selected;

    if (!selected) {
      return;
    } else {
      this.downloadChosenFile('fw');
    }
  }

  //---------------------function when firmware file selected------------------------------//

  public onParaFileSelected(event: SelectCustomEvent): void {
    const selected = event.detail.value;
    this.parameterFileFB = selected;
    this.paraFileSelected = !!selected;

    if (!selected) {
      return;
    } else {
      this.downloadChosenFile('pm');
    }
  }

  public async downloadChosenFile(type: string) {
    // console.log(type);
    if (type === 'fw') {
      if (this.isOnline) {
        /*! for online */
        const firmwareUpgrade: IFirmwareUpgrade = {
          version: '1.0.0',
          fullPath: this.softwareFileFB.fullPath,
          name: this.softwareFileFB.name,
        };
        try {
          const downloadFile = httpsCallable<{ path: string }, IFirmwareFile>(this.functions, 'downloadJsonFile');

          const result = await downloadFile({ path: firmwareUpgrade.fullPath });
          const jsonData = result.data;

          /*const jsonData = await this.httpClient
            .get<IFirmwareFile>(environment.functions.storageDownload, {
              responseType: 'json',
              params: { path: firmwareUpgrade.fullPath },
            })
            .toPromise();*/

          const fileIsSaved = await this.storageService.fileWrite(jsonData, firmwareUpgrade.fullPath);
          if (fileIsSaved) {
            this.fileSelected = true;
            //  console.log('Your file has been downloaded', this.fileSelected);
          } else {
            this.fileSelected = false;
            // console.log('Something went wrong while downloading');
          }

          this.firmware = jsonData;
        } catch (error) {
          console.error('Error downloading file:', error);
          this.fileSelected = false;
        }
      }
    }

    // added for parameter upload
    if (type === 'pm') {
      if (this.isOnline) {
        /*! for online */
        const parameterUpgrade: IParameterUpgrade = {
          version: '1.0.0',
          fullPath: this.parameterFileFB.fullPath,
          name: this.parameterFileFB.name,
        };
        try {
          const downloadFile = httpsCallable<{ path: string }, IParameterFile>(this.functions, 'downloadJsonFile');

          const result = await downloadFile({ path: parameterUpgrade.fullPath });
          const jsonData = result.data;

          /*
          const jsonData = await this.httpClient
            .get<IParameterFile>(environment.functions.storageDownload, {
              responseType: 'json',
              params: { path: parameterUpgrade.fullPath },
            })
            .toPromise();
         */
          const fileIsSaved = await this.storageService.fileWrite(jsonData, parameterUpgrade.fullPath);
          if (fileIsSaved) {
            this.paraFileSelected = true;
            //  console.log('Your file has been downloaded', this.fileSelected);
          } else {
            this.paraFileSelected = false;
            // console.log('Something went wrong while downloading');
          }

          this.parameterFile = jsonData;
        } catch (error) {
          console.error('Error downloading file:', error);
          this.paraFileSelected = false;
        }
      }
    }
  }

  public closeDialog() {
    this.fileSelected = false;

    this.paraFileSelected = false;
    this.softwareFileFB = null;
    this.parameterFileFB = null;
    this.paraItems = [];
    this.items = [];
    this.modalService.dismissModal('FirmwareDownloadComponent');
  }
}
